import React, { useMemo } from 'react';
import moment from 'moment';
import { DatePicker } from '@packages/ui/composites/DatePicker';
import { DATE_FORMAT } from '@packages/shared/constants';
import type { FilterDef } from '../../schema/types';
import { TABLE_IDS } from '../../semantic-contract';

function resolveDateRangeEndField(f: FilterDef): string {
  return f.rangeEndField != null && f.rangeEndField !== '' ? f.rangeEndField : `${f.name}End`;
}

function parseToDate(raw: unknown): Date | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const m = moment(String(raw));
  return m.isValid() ? m.toDate() : null;
}

interface DateRangeFilterProps {
  filter: FilterDef;
  pendingFilter: Record<string, unknown>;
  onFilterChange: (name: string, value: unknown) => void;
}

export function DateRangeFilter({
  filter,
  pendingFilter,
  onFilterChange,
}: DateRangeFilterProps): JSX.Element {
  const endField = resolveDateRangeEndField(filter);
  const dependency = filter.dependsOn;
  const depMet = dependency ? Boolean(pendingFilter[dependency]) : true;
  const endPlaceholder = filter.rangeEndPlaceholder ?? 'End date';

  const startDate = useMemo(() => parseToDate(pendingFilter[filter.name]), [pendingFilter[filter.name]]);
  const endDate = useMemo(() => parseToDate(pendingFilter[endField]), [pendingFilter[endField]]);

  const minDate = useMemo(
    () => (filter.minDate ? moment(filter.minDate).toDate() : undefined),
    [filter.minDate],
  );
  const maxDate = useMemo(
    () => (filter.maxDate ? moment(filter.maxDate).toDate() : undefined),
    [filter.maxDate],
  );

  return (
    <div className='flex flex-col gap-2'>
      <DatePicker
        name={filter.name}
        placeholder={filter.placeholder ?? DATE_FORMAT}
        value={startDate}
        onChange={(date: Date | null) => {
          onFilterChange(filter.name, date ? moment(date).format(DATE_FORMAT) : undefined);
        }}
        disabled={!depMet}
        minDate={minDate}
        maxDate={maxDate}
        portalToBody
        calendarDensity='production'
        semanticId={TABLE_IDS.filterDate(filter.name)}
      />
      <DatePicker
        name={endField}
        placeholder={endPlaceholder}
        value={endDate}
        onChange={(date: Date | null) => {
          onFilterChange(endField, date ? moment(date).format(DATE_FORMAT) : undefined);
        }}
        disabled={!depMet}
        minDate={minDate}
        maxDate={maxDate}
        portalToBody
        calendarDensity='production'
        semanticId={TABLE_IDS.filterDate(endField)}
      />
    </div>
  );
}

export { resolveDateRangeEndField };
